import { error, log } from "./logger";

// get size bit to kb and append 'kb' to it
export function getSize(size: number) {
  return (size / 1024).toFixed(2) + " kB";
}

// return sample rate into ration 40 into 0.4
export function getSampleRateRatio(sampleRate?: number) {
  let defaultSampleRate = 40;
  if (sampleRate && sampleRate >= 1 && sampleRate <= 100) {
    defaultSampleRate = sampleRate;
  }
  return defaultSampleRate / 100;
}

// get path after last slash
export function getLastPathWithQuery(urlString: string) {
  try {
    const url = new URL(urlString);
    const pathSegments = url.pathname.split("/").filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1] || "";
    if (lastSegment === "") {
      return url.pathname + url.search;
    }
    return lastSegment + url.search;
  } catch (e) {
    log(e);
    error("Invalid URL:", urlString);
    return urlString;
  }
}

// fold paths on UUIDs and numeric IDs with '?'
export function getFoldedPath(pathname: string): string {
  if (pathname === "/") return "/";

  // Normalize: replace multiple slashes with a single slash, remove trailing slashes (except root)
  let normalized = pathname.replace(/\/+/g, "/");
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.replace(/\/+$/, "");
  }

  // Split the path into segments
  const segments = normalized.split("/");
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const numberRegex = /^\d+$/;
  const folded = segments.map((seg) => {
    if (uuidRegex.test(seg) || numberRegex.test(seg)) {
      return "?";
    }
    return seg;
  });

  // Remove empty segments except for root
  const result = folded.filter((seg, idx) => seg !== "" || idx === 0).join("/");
  return result;
}

/**
 * Listen for navigation events (SPA and traditional) and call the callback when pathname or hash changes.
 * Ignores search param changes. Debounced to avoid duplicate calls.
 */
export function onNavigation(callback: () => void) {
  let lastPath = window.location.pathname;
  let lastHash = window.location.hash;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  const handleNavigation = () => {
    const currentPath = window.location.pathname;
    const currentHash = window.location.hash;
    if (currentPath !== lastPath || currentHash !== lastHash) {
      lastPath = currentPath;
      lastHash = currentHash;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        callback();
      }, 10);
    }
  };

  window.addEventListener("popstate", handleNavigation);
  window.addEventListener("hashchange", handleNavigation);

  // Monkey-patch pushState and replaceState for SPA navigation
  const origPushState = history.pushState;
  const origReplaceState = history.replaceState;
  history.pushState = function (...args) {
    const result = origPushState.apply(this, args);
    window.dispatchEvent(new Event("l9rum:navigation"));
    return result;
  };
  history.replaceState = function (...args) {
    const result = origReplaceState.apply(this, args);
    window.dispatchEvent(new Event("l9rum:navigation"));
    return result;
  };
  window.addEventListener("l9rum:navigation", handleNavigation);
}
